import React, { useEffect, useRef, useState } from 'react';
import { NmiCardFields, isNmiGateway, gatewayIsReady } from './nmiKit.jsx';
import { Get } from '../../hooks/useFetch';
import { SendRequest } from '../../hooks/usePost';
import { SaveUser } from '../../features/authentication/hooks/useToken.jsx';
import { loginUrl, storeUrl } from '../../util/maskedDomain';
import { money, loadAcceptJs, dispatchAcceptData, DEFAULT_HOUSE_SPONSOR_ID, MYUS_RADIO_CSS } from './editKit';
import { StorePhoneField, toE164, phoneProblem } from './phoneField';
import { SubscriptionPriceTag, lineDiscountFor, cartDiscountPct, maxQtyFor } from './cartKit';
import { checkoutBackUrl } from './checkoutEntry';
import { subForPage } from './storefrontPages';
import { FreeShipNotice, FreeShipBanner, readFreeShip } from './freeShipping.jsx';
import { checkoutTheme, checkoutVariant, CHECKOUT_GRID_CSS, ckColumns } from './checkoutSkin';
import CheckoutTimer from './checkoutTimer';
import CheckoutDelivery from './checkoutDelivery';
import { LowStockPill, readLowStock, lowStockMap, firstOfProduct, qtyOfProduct } from './lowStock.jsx';
import { CtaSubline, readCtaBadge, CTA_STACK } from './ctaBadge.jsx';
import { PAYMENT_MARKS } from './md2FooterIcons';
import { identifyEmail, trackPurchase, attributionFields, trackSignUp, trackAddPaymentInfo, trackingCartSnapshot } from './tracking';
import UsAddressFields from '../../components/usAddressFields.jsx';
import AddressAutocomplete from '../../components/addressAutocomplete.jsx';

// Custom checkbox (matches the checkout shipping-method radio's visual
// language — gold fill, white check popping in with a spring ease) for the
// "light" (guest-checkout) SponsorPicker variant. The "wizard" variant
// already has its own modern circular teal toggle and is untouched.
const MYUS_CHECKBOX_CSS = `
.myus-checkbox-input { position: absolute; opacity: 0; width: 20px; height: 20px; margin: 0; cursor: pointer; }
.myus-checkbox-box {
  position: relative; width: 18px; height: 18px; border-radius: 5px; flex: none;
  border: 2px solid rgba(201, 168, 106, 0.4);
  transition: border-color .18s ease, background-color .18s ease, transform .12s ease;
}
.myus-checkbox-box svg {
  position: absolute; top: 50%; left: 50%; width: 12px; height: 12px;
  transform: translate(-50%, -50%) scale(0); transition: transform .22s cubic-bezier(.34, 1.56, .64, 1);
}
.myus-checkbox-input:checked + .myus-checkbox-box { background: rgb(201, 168, 106); border-color: rgb(201, 168, 106); }
.myus-checkbox-input:checked + .myus-checkbox-box svg { transform: translate(-50%, -50%) scale(1); }
.myus-checkbox-input:active + .myus-checkbox-box { transform: scale(0.88); }
.myus-checkbox-input:focus-visible + .myus-checkbox-box { box-shadow: 0 0 0 3px rgba(201, 168, 106, 0.25); }
`;

// Debounced sponsor search against the redacted public Find endpoint —
// returns only {id, fullName, webAlias} for Members/Influencers, never the
// full customer doc the authenticated /Customers/Find exposes.
//
// variant='light' (default, Part 4 guest checkout — unchanged) keeps the
// caller-supplied `input` style verbatim and a plain square checkbox.
// variant='wizard' (Part 5 enrollment wizard, dark backdrop) forces its own
// light input styling — matching the reference's explicit
// `.arego-brand-partner-search{color:#111827!important;background:#fff!important}`
// override — since the wizard's own dark-theme input style is unreadable
// here (white text on a translucent white box), plus a circular teal
// checkbox matching the reference's `.arego-no-referral-visual`.
export const SponsorPicker = ({ sponsorId, setSponsorId, sponsorLabel, setSponsorLabel, input, t, variant = 'light', locked = false }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [notReferred, setNotReferred] = useState(false);
  const wizard = variant === 'wizard';
  const searchInput = wizard ? { ...input, background: '#fff', color: '#111827', border: '1px solid rgba(0,0,0,.15)' } : input;

  useEffect(() => {
    if (notReferred || !q.trim()) { setResults([]); return; }
    const handle = setTimeout(() => {
      Get(`/api/v1/public/Customers/Find?search=${encodeURIComponent(q.trim())}`,
        (r) => setResults(Array.isArray(r) ? r : []), () => setResults([]));
    }, 300);
    return () => clearTimeout(handle);
  }, [q, notReferred]);

  // "Not referred by anyone" sets a fixed house sponsor id — look up and show
  // its real name/webAlias rather than leaving the field blank, and lock it
  // (no × to clear) until the box is unchecked.
  useEffect(() => {
    if (!notReferred) return;
    Get(`/api/v1/public/Customers/Find?id=${encodeURIComponent(DEFAULT_HOUSE_SPONSOR_ID)}`, (r) => {
      const hit = Array.isArray(r) ? r[0] : null;
      setSponsorLabel(hit ? `${hit.fullName}${hit.webAlias ? ` (${hit.webAlias})` : ''}` : 'House account');
    }, () => setSponsorLabel('House account'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notReferred]);

  const pick = (r) => { setSponsorId(r.id); setSponsorLabel(`${r.fullName}${r.webAlias ? ` (${r.webAlias})` : ''}`); setQ(''); setResults([]); };

  return <div style={{ marginBottom: 6 }}>
    <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, marginBottom: 4, color: wizard ? '#fff' : t.muted }}>Sponsor</label>
    {sponsorId && <div style={{ ...searchInput, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, opacity: notReferred ? 0.75 : 1 }}>
      <span>{sponsorLabel || '…'}</span>
      {!notReferred && !locked && <span onClick={() => { setSponsorId(''); setSponsorLabel(''); }} style={{ cursor: 'pointer', color: wizard ? '#111827' : t.muted }}>×</span>}
    </div>}
    {locked && <div style={{ fontSize: 12, color: wizard ? 'rgba(255,255,255,.8)' : t.muted, marginBottom: 6 }}>
      Sponsor is set by your coupon — remove the coupon to change it.
    </div>}
    {!notReferred && !sponsorId && <div style={{ position: 'relative' }}>
      <input style={searchInput} placeholder="Search by name, email or web alias" value={q} onChange={(e) => setQ(e.target.value)} />
      {results.length > 0 && <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid rgba(0,0,0,.12)', borderRadius: 8, marginTop: 4, zIndex: 5, maxHeight: 180, overflowY: 'auto' }}>
        {results.map((r) => <div key={r.id} onClick={() => pick(r)} style={{ padding: '8px 10px', cursor: 'pointer', fontSize: 13.5, color: '#111827', borderBottom: '1px solid rgba(0,0,0,.06)' }}>
          {r.fullName}{r.webAlias ? <span style={{ color: t.muted }}> ({r.webAlias})</span> : ''}
        </div>)}
      </div>}
    </div>}
    {!locked && <label style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12.5, color: wizard ? '#fff' : t.muted, cursor: 'pointer' }}>
      {wizard ? <span onClick={() => {
          const v = !notReferred;
          setNotReferred(v);
          if (v) { setSponsorId(DEFAULT_HOUSE_SPONSOR_ID); setQ(''); setResults([]); }
          else { setSponsorId(''); setSponsorLabel(''); }
        }} style={{
          width: 22, height: 22, minWidth: 22, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          border: `2px solid ${notReferred ? '#0f766e' : '#c4c4c4'}`, background: notReferred ? '#0f766e' : '#fff', transition: 'background .15s,border-color .15s',
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ opacity: notReferred ? 1 : 0, transition: 'opacity .15s' }}>
            <path d="M4 12l6 6L20 6" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        : <>
          <style>{MYUS_CHECKBOX_CSS}</style>
          <input className="myus-checkbox-input" type="checkbox" checked={notReferred} onChange={(e) => {
            const v = e.target.checked;
            setNotReferred(v);
            if (v) { setSponsorId(DEFAULT_HOUSE_SPONSOR_ID); setQ(''); setResults([]); }
            else { setSponsorId(''); setSponsorLabel(''); }
          }} />
          <span className="myus-checkbox-box">
            <svg viewBox="0 0 24 24" fill="none"><path d="M4 12l6 6L20 6" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </span>
        </>}
      I was not referred by anyone
    </label>}
  </div>;
};

// Guest checkout — the additional path Checkout renders instead of the
// "please log in as a member" gate when !scope && skin.mkt.guestCheckoutEnabled.
// Reuses the parent's already-resolved cart rows/quote (one call site for
// cart quoting, not duplicated here) and posts straight to the new public
// guest-checkout endpoint: pay -> create account -> create order, with a
// void/refund safety net entirely server-side. On success the response
// includes a ready-to-use login, persisted immediately so the guest lands on
// their new Dashboard already signed in.
// The +/− stepper, sized to stay tappable on a phone.
const qtyBtn = (t) => ({
  width: 26, height: 26, lineHeight: '24px', padding: 0, borderRadius: 7, cursor: 'pointer',
  background: 'transparent', color: t.ink, border: `1px solid ${t.dark ? 'rgba(255,255,255,.22)' : 'rgba(0,0,0,.18)'}`,
  fontSize: 15, fontWeight: 600,
});

export const GuestCheckout = ({ skin, slug, rows, quote, subQuotes = [], rowInterval, coupon, setCoupon, appliedCoupon, applyCoupon,
  sponsorId, setSponsorId, sponsorLabel, setSponsorLabel, sponsorLocked = false, country, setCountry,
  shippingMethodId, setShippingMethodId, cart, navigate }) => {
  // Same rule as the member checkout: the palette follows where the cart was
  // filled, not only the store template.
  const t = checkoutTheme(skin, slug);
  const ckNatural = checkoutVariant(slug, skin) === 'natural';
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', webAlias: '', address: '', city: '', state: '', zip: '', phone: '', phoneCountry: 'US' });
  // Marketing consent: unticked unless they tick it. True records consent for
  // this store; false records nothing.
  const [emailOptIn, setEmailOptIn] = useState(false);
  // The store can require a phone regardless of destination (Marketplace →
  // Require phone at checkout); a MyUS-forwarded parcel always needs one.
  // Resolved server-side from the global Advance Setting and the store's
  // override, so the precedence lives in one place. MyUS always forces it:
  // without a phone their API refuses the order after the card is charged.
  const phoneRequired = skin.checkoutPhone !== 'optional' || !!quote?.shippingMyus;
  // Sponsor state lives in the parent Checkout (storefront.jsx) so the quote
  // carries it for the Sponsor Coupon Restriction check; the referral-link
  // pre-seed happens there too. Country lives there too, same reason — the
  // parent's shared quote effect needs it to pick the right shipping zone.
  // A guest has no auth token yet, and /graphql requires one — the
  // authenticated `countries` GraphQL query silently returns nothing here,
  // which is why this used to render an empty, option-less <select>. The
  // public REST twin of the same reference data (already built for the
  // enroll wizard's address step, before any account/token exists) works
  // for a guest.
  const [countries, setCountries] = useState([]);
  useEffect(() => {
    Get('/api/v1/public/Countries', (list) => setCountries((Array.isArray(list) ? list : []).filter((c) => c.active !== false)), () => { });
  }, []);
  const [card, setCard] = useState({ number: '', expiry: '', code: '' });
  const nmiRef = useRef(null); // NMI (Collect.js) card fields, when the store's gateway is NMI
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);
  // Influencer signups (guestSignupType "3") pick a username (webAlias) —
  // their referral-link name. Registered-customer stores ("2") never ask.
  const isInfluencerSignup = String(skin.mkt.guestSignupType ?? '2') === '3';
  const [aliasStatus, setAliasStatus] = useState(null); // 'checking'|'available'|'taken'|'invalid'|null
  useEffect(() => {
    if (!isInfluencerSignup) return undefined;
    const alias = form.webAlias.trim().toLowerCase();
    if (alias === '') { setAliasStatus(null); return undefined; }
    if (!/^[a-z0-9-]{3,40}$/.test(alias)) { setAliasStatus('invalid'); return undefined; }
    setAliasStatus('checking');
    const timer = setTimeout(() => {
      Get(`/api/v1/public/Marketplaces/${skin.mkt.id}/check-webalias?webAlias=${encodeURIComponent(alias)}`,
        (res) => setAliasStatus(res?.available ? 'available' : 'taken'),
        () => setAliasStatus(null));
    }, 400);
    return () => clearTimeout(timer);
  }, [form.webAlias, isInfluencerSignup, skin.mkt.id]);

  // Live "does this email already have an account" check — the backend has
  // always rejected a duplicate email at final submit (pre-charge), but
  // that meant a returning customer only found out after filling in their
  // address AND card details. Same debounced-probe pattern as the webAlias
  // check above, reusing the endpoint already built for the enroll wizard.
  const [emailStatus, setEmailStatus] = useState(null); // 'checking'|'available'|'taken'|null
  useEffect(() => {
    const email = form.email.trim();
    if (!email || !email.includes('@')) { setEmailStatus(null); return undefined; }
    setEmailStatus('checking');
    const timer = setTimeout(() => {
      // Same debounce, two jobs: tell Brevo who this is, so a checkout that
      // is started and then abandoned can still be followed up.
      identifyEmail(email);
      Get(`/api/v1/public/Marketplaces/${skin.mkt.id}/check-email?email=${encodeURIComponent(email)}`,
        (res) => setEmailStatus(res?.available === false ? 'taken' : 'available'),
        () => setEmailStatus(null));
    }, 400);
    return () => clearTimeout(timer);
  }, [form.email, skin.mkt.id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const gatewayReady = gatewayIsReady(skin.gateway);
  const nmi = isNmiGateway(skin.gateway);
  // Only offer countries the store's zones cover (skin.shippingCountries;
  // null = no restriction). The quote's shippingOk stays the backstop for
  // anything that slips through (stale state, older API without the list).
  const shippable = skin.shippingCountries ? countries.filter((c) => skin.shippingCountries.includes(c.iso2)) : countries;
  const noShip = quote && quote.shippingOk === false;
  const countryName = (iso) => countries.find((c) => c.iso2 === iso)?.name ?? iso;

  // walletToken: set when an Apple Pay / Google Pay sheet (NMI) completed.
  const submit = async (walletToken) => {
    setError(null);
    { const snap = trackingCartSnapshot(cart, skin); trackAddPaymentInfo(snap.items, snap.value, 'card'); }
    if (!form.firstName || !form.lastName || !form.email || !form.password) { setError('First name, last name, email and password are required.'); return; }
    if (emailStatus === 'taken') { setError('An account with this email already exists — please log in instead.'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (isInfluencerSignup) {
      const alias = form.webAlias.trim().toLowerCase();
      if (!/^[a-z0-9-]{3,40}$/.test(alias)) { setError('Please choose a username: 3-40 characters, lowercase letters, numbers and dashes.'); return; }
      if (aliasStatus === 'taken') { setError('That username is already taken — please choose another.'); return; }
    }
    if (!form.address) { setError('A shipping address is required.'); return; }
    // MyUS forwards international parcels and needs a recipient phone; some
    // stores require one on every order.
    // When optional, a blank phone is fine — but a half-typed one is not:
    // it would reach ShipStation and MyUS as an unusable number.
    const phoneTyped = String(form.phone || '').replace(/\D/g, '') !== '';
    if (phoneRequired || phoneTyped) {
      const problem = phoneProblem(form.phoneCountry, form.phone);
      if (problem) {
        setError(quote?.shippingMyus && !phoneTyped
          ? 'A phone number is required for international delivery.'
          : problem);
        return;
      }
    }
    // Block BEFORE the card is tokenized — the backend would refuse this
    // order anyway (shippingOk gate), but with a generic error after the
    // shopper already typed their card in.
    if (noShip) { setError(`We can't ship to ${countryName(country)} — please choose another country.`); return; }
    if (!sponsorId) { setError('Search for a sponsor or check "I was not referred by anyone."'); return; }
    if (!gatewayReady) { setError('The payment gateway is not configured yet.'); return; }
    const expDigits = card.expiry.replace(/\D/g, '');
    const expMonth = expDigits.slice(0, 2);
    const expYear = expDigits.length >= 4 ? '20' + expDigits.slice(2, 4) : '';
    if (!nmi && (!card.number || !card.code || expDigits.length < 4)) { setError('Please fill in the card details.'); return; }
    if (!nmi && (Number(expMonth) < 1 || Number(expMonth) > 12)) { setError('The expiry month must be between 01 and 12.'); return; }

    setPaying(true);
    try {
      let opaque;
      if (nmi) {
        opaque = walletToken?.dataValue ? walletToken : await nmiRef.current.tokenize();
      } else {
        const Accept = await loadAcceptJs(skin.gateway.sandboxMode === true);
        const secureData = {
          authData: { clientKey: skin.gateway.publicClientKey, apiLoginID: skin.gateway.apiLoginId },
          cardData: { cardNumber: card.number.replace(/\s+/g, ''), month: expMonth, year: expYear, cardCode: card.code },
        };
        const response = await dispatchAcceptData(Accept, secureData);
        if (response.messages.resultCode === 'Error') {
          setPaying(false);
          setError(response.messages.message?.[0]?.text ?? 'The card could not be tokenized.');
          return;
        }
        opaque = response.opaqueData;
      }
      SendRequest('POST', `/api/v1/public/Marketplaces/${skin.mkt.id}/guest-checkout`, {
        items: rows.map((i) => ({ productId: i.productId, quantity: Number(i.quantity) || 1, subscribe: !!i.subscribe })),
        couponCode: appliedCoupon,
        customerId: '',
        // Top-level — computeQuote reads req.quoteReq.Country/ShippingMethodID
        // directly (embedded struct), not customer.country, so both must be
        // set here too or the final charge could quote a different
        // zone/price than what was just shown on screen.
        country: country || 'US',
        shippingMethodId,
        customer: { firstName: form.firstName, lastName: form.lastName, email: form.email, webAlias: isInfluencerSignup ? form.webAlias.trim().toLowerCase() : '', address: form.address, city: form.city, state: form.state, zip: form.zip, country: country || 'US', phone: toE164(form.phoneCountry, form.phone), phoneCountry: form.phoneCountry },
        payment: { dataDescriptor: opaque.dataDescriptor, dataValue: opaque.dataValue },
        password: form.password,
        sponsorId,
        emailOptIn,
        ...attributionFields(),
      }, (res) => {
        // The guest checkout also creates the account — GA4 sign_up.
        trackSignUp('storefront');
        trackPurchase({
          orderId: res.orderId, value: quote?.total, shipping: quote?.shipping,
          coupon: quote?.couponValid ? appliedCoupon : '', checkoutType: 'guest',
          email: form.email, name: `${form.firstName} ${form.lastName}`, phone: toE164(form.phoneCountry, form.phone),
          items: rows.map((i) => ({ id: i.productId, name: i.p?.name, price: i.p?.price, quantity: Number(i.quantity) || 1, variant: i.subscribe ? 'subscription' : 'one-time' })),
        });
        cart.clear();
        if (res.login) SaveUser(res.login);
        // storeUrl keeps masked domains on root paths (the old hard-coded
        // /marketplace/ prefix bounced through MaskedPrefixRedirect), and the
        // sub flag matches the member checkout's thankyou.
        navigate(storeUrl(slug, `thankyou?order=${encodeURIComponent(res.orderId)}${(res.subscriptionIds ?? []).length || res.subscriptionId ? '&sub=1' : ''}`));
      }, (body) => {
        setPaying(false);
        let msg = '';
        try { msg = JSON.parse(body)?.error ?? ''; } catch { msg = ''; }
        setError(msg || 'Checkout failed.');
      });
    } catch (e) {
      setPaying(false);
      setError(String(e.message ?? e));
    }
  };

  const panel = { background: t.dark ? t.card : '#fff', border: t.dark ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.08)', borderRadius: 12, padding: '12px 14px' };
  const input = { background: t.dark ? '#1a1d22' : '#fff', border: t.dark ? '1px solid rgba(255,255,255,.15)' : '1px solid rgba(0,0,0,.15)', borderRadius: 8, padding: '9px 11px', color: t.dark ? '#fff' : t.ink, width: '100%', boxSizing: 'border-box', fontSize: 14 };

  const ls = readLowStock(skin.mkt);
  const lsMap = lowStockMap(ls);
  const lsRows = ls.enabled && ls.checkoutPage ? firstOfProduct(rows) : new Set();
  // The sub-line inside the pay button. Armed by the campaign alone so the row
  // is reserved and the button never changes height; what it says is decided
  // below against the button's own disabled expression. This is the path most
  // logged-out shoppers take, so it is patched alongside the member checkout or
  // the campaign disappears for the majority.
  const cta = readCtaBadge(skin.mkt);
  const ctaOn = cta.enabled && cta.checkoutButton && !skin.isHipaa;
  const ctaPct = ctaOn ? cartDiscountPct(rows) : 0;

  const fsCfg = readFreeShip(skin.mkt);
  const L = ckNatural ? 'ck-left' : undefined;
  const R = ckNatural ? 'ck-right' : undefined;

  const body = <div className={ckNatural ? 'ck-natural' : undefined}
    style={ckNatural ? undefined : { maxWidth: 560, margin: '0 auto', padding: '26px 22px' }}>
    {ckNatural && <style>{CHECKOUT_GRID_CSS}</style>}
    <div className={ckNatural ? 'ck-head' : undefined}>
      {ckNatural && <div className="ck-bar">
        <a href={checkoutBackUrl(slug, subForPage, storeUrl)}>&lsaquo; Continue shopping</a>
        <span style={{ fontSize: 12.5, color: '#6E685F', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          Encrypted payment
        </span>
      </div>}
      {ckNatural && <h1 style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-.01em', margin: '0 0 14px', color: t.ink }}>Checkout</h1>}
      <CheckoutTimer mkt={skin.mkt} t={t} />
      <FreeShipBanner cfg={fsCfg} quote={quote} destinationKnown={!!country} isHipaa={skin.isHipaa} dark={t.dark} />
      {ckNatural
        ? <p className="ck-lede">Create your account as you pay — it takes a moment and keeps your order history in one place.{skin.gateway.sandboxMode === true && <span style={{ color: '#d97706', fontWeight: 700 }}> · SANDBOX MODE</span>}</p>
        : <div style={{ fontSize: 13, color: t.muted, marginBottom: 12 }}>Guest checkout 🔒 {skin.gateway.sandboxMode === true && <span style={{ color: '#d97706', fontWeight: 700 }}>· SANDBOX MODE</span>}</div>}
    </div>

    <div className={R}>

    {rows.map((i) => <div key={i.productId + (i.subscribe ? '_s' : '')} style={{ ...panel, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
      <span style={{ width: 46, height: 46, borderRadius: 8, background: t.dark ? 'rgba(255,255,255,.06)' : '#f4f3f0', flex: 'none', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {i.p.imageUrl && <img src={i.p.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#fff' }} />}
      </span>
      {/* minWidth floors the name column so the qty/price group wraps to its
          own line on a phone rather than crushing the name. */}
      <span style={{ fontSize: 14, fontWeight: 600, flex: 1, minWidth: 120 }}>
        {i.p.name}{i.subscribe && <span style={{ color: t.accent }}> · Subscription</span>}
        {i.subscribe && rowInterval && <span style={{ display: 'block', fontSize: 11.5, fontWeight: 400, color: t.muted }}>Renews every {rowInterval(i)} days</span>}
        {lsRows.has(i.productId + (i.subscribe ? '_s' : '')) && <span style={{ display: 'block' }}><LowStockPill
          cfg={ls} map={lsMap} productId={i.productId} qty={qtyOfProduct(rows, i.productId)}
          dark={t.dark === true} accent={t.accent} ink={t.ink} isHipaa={skin.isHipaa} /></span>}
      </span>
      {/* A guest could see the cart but not change it — same controls as the
          member checkout, so a shopper who wants two bottles (or changed their
          mind) doesn't have to go back to the store. Any change re-quotes
          automatically (the quote effect keys on the cart lines). */}
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
        {cart && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button type="button" title="Fewer" aria-label={`Fewer ${i.p.name}`}
            onClick={() => cart.setQty(i.productId, i.subscribe, Math.max(1, Number(i.quantity) - 1))}
            style={qtyBtn(t)}>−</button>
          <span style={{ minWidth: 20, textAlign: 'center', fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>{i.quantity}</span>
          <button type="button" title="More" aria-label={`More ${i.p.name}`}
            disabled={maxQtyFor(skin, i.productId) > 0 && i.quantity >= maxQtyFor(skin, i.productId)}
            onClick={() => cart.setQty(i.productId, i.subscribe, Number(i.quantity) + 1)}
            style={qtyBtn(t)}>+</button>
          {maxQtyFor(skin, i.productId) > 0 && i.quantity >= maxQtyFor(skin, i.productId)
            && <span style={{ fontSize: 11, color: t.muted }}>Max {maxQtyFor(skin, i.productId)}</span>}
        </span>}
        <SubscriptionPriceTag
          price={lineDiscountFor(i.p, i.subscribe).was * i.quantity}
          subscriptionPrice={lineDiscountFor(i.p, i.subscribe).now * i.quantity} />
        {cart && <span onClick={() => cart.remove(i.productId, i.subscribe)} title="Remove"
          role="button" aria-label={`Remove ${i.p.name}`}
          style={{ cursor: 'pointer', color: t.muted, fontSize: 17, lineHeight: 1 }}>×</span>}
      </span>
    </div>)}

      <CheckoutDelivery mkt={skin.mkt} quote={quote} t={t} money={money} />
    </div>

    <div className={L}>
    <div style={{ ...panel, marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: t.accent }}>Create your account</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input style={input} placeholder="First name" value={form.firstName} onChange={set('firstName')} />
        <input style={input} placeholder="Last name" value={form.lastName} onChange={set('lastName')} />
      </div>
      <div>
        <input style={input} type="email" placeholder="Email" value={form.email} onChange={set('email')} />
        {emailStatus === 'taken' && <div style={{ fontSize: 11.5, marginTop: 4, color: '#b91c1c' }}>
          An account with this email already exists —{' '}
          <a href={loginUrl()} style={{ color: '#b91c1c', fontWeight: 700, textDecoration: 'underline' }}>log in instead</a>.
        </div>}
        {emailStatus === 'checking' && <div style={{ fontSize: 11.5, marginTop: 4, color: t.muted }}>Checking…</div>}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8, fontSize: 12.5, lineHeight: 1.4, color: t.muted, cursor: 'pointer' }}>
          <input type="checkbox" checked={emailOptIn} onChange={(e) => setEmailOptIn(e.target.checked)} name="emailOptIn"
            style={{ width: 15, height: 15, marginTop: 1, flex: 'none', accentColor: t.accent }} />
          <span>Email me news and offers from {skin.mkt.name || 'this store'}</span>
        </label>
      </div>
      <input style={input} type="password" placeholder="Password (min. 8 characters)" value={form.password} onChange={set('password')} />
      {isInfluencerSignup && <div>
        <input style={input} placeholder="Username (your referral link name)" value={form.webAlias}
          onChange={(e) => setForm((f) => ({ ...f, webAlias: e.target.value.toLowerCase() }))} />
        <div style={{ fontSize: 11.5, marginTop: 4, color:
          aliasStatus === 'available' ? '#15803d' : aliasStatus === 'taken' || aliasStatus === 'invalid' ? '#b91c1c' : t.muted }}>
          {aliasStatus === 'available' ? '✓ This username is available'
            : aliasStatus === 'taken' ? '✗ Already taken — please choose another'
            : aliasStatus === 'invalid' ? '3-40 characters: lowercase letters, numbers and dashes'
            : aliasStatus === 'checking' ? 'Checking availability…'
            : '3-40 characters: lowercase letters, numbers and dashes'}
        </div>
      </div>}
      <div>
        <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, marginBottom: 4, color: t.muted }}>Country</label>
        {/* Real country picker, not a hardcoded "US" — without this a guest's
            shipping zone/rate can never be matched correctly for anywhere
            outside the US (see PickShippingZone), and the running quote
            below stays wrong until they submit. */}
        <select style={input} value={country} onChange={(e) => setCountry(e.target.value)}>
          {shippable.map((c) => <option key={c.iso2} value={c.iso2}>{c.name}</option>)}
          {/* Keep an out-of-list pick visible (stale state) instead of the
              select silently showing the wrong country. */}
          {country && !shippable.some((c) => c.iso2 === country) && <option value={country}>{countryName(country)}</option>}
        </select>
        {noShip && <div style={{ fontSize: 12, color: '#d63939', marginTop: 4 }}>We can&apos;t ship to {countryName(country)} — please choose another country.</div>}
      </div>
      <AddressAutocomplete enabled={!!skin?.mkt?.addressAutocomplete} country={country} value={form.address} inputStyle={input} dark={!!t.dark}
        onChange={(v) => setForm((f) => ({ ...f, address: v }))}
        onPick={({ address, city, state, zip }) => setForm((f) => ({ ...f, address, city, state, zip }))} />
      <UsAddressFields country={country} city={form.city} state={form.state} zip={form.zip} inputStyle={input} dark={!!t.dark}
        onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} />
      {/* The country is picked, never typed: a hand-typed dial code is the
          single most common way an international number arrives unusable,
          and MyUS rejects the order outright when it does. */}
      <div>
        <StorePhoneField
          skin={skin}
          theme={t}
          required={phoneRequired}
          country={form.phoneCountry}
          number={form.phone}
          onCountry={(iso) => setForm((f) => ({ ...f, phoneCountry: iso }))}
          onNumber={(n) => setForm((f) => ({ ...f, phone: n }))}
          help={quote?.shippingMyus
            ? 'The carrier uses this for delivery and customs.'
            : 'Used to reach you about this order.'}
        />
      </div>
      <SponsorPicker sponsorId={sponsorId} setSponsorId={setSponsorId} sponsorLabel={sponsorLabel} setSponsorLabel={setSponsorLabel} input={input} t={t} locked={sponsorLocked} />
    </div>

    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
      <input style={input} placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value)} />
      <button onClick={applyCoupon} style={{ ...input, width: 110, cursor: 'pointer', fontWeight: 600, textAlign: 'center' }}>Apply</button>
    </div>
    {quote?.couponMessage && !quote?.couponValid && appliedCoupon && <div style={{ fontSize: 12, color: '#d63939', marginBottom: 8 }}>{quote.couponMessage}</div>}
    {quote?.couponValid && <div style={{ fontSize: 12, color: '#2f9e6e', marginBottom: 8 }}>Coupon applied — {quote.discountPct}% off products (shipping is not discounted).</div>}

    {nmi ? <div style={{ marginBottom: 12 }}>
      <NmiCardFields ref={nmiRef} gateway={skin.gateway} inputStyle={input} dark={t.dark}
        total={quote?.total} wallets={!rows.some((i) => i.subscribe)} onWalletToken={(tok) => { if (!paying) submit(tok); }} />
      <div style={{ fontSize: 11, color: t.muted, marginTop: 6 }}>Card details are entered in secure fields hosted by our payment processor — they never touch our servers.</div>
    </div> : <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
      <input style={input} placeholder="Card number" value={card.number} onChange={(e) => setCard((c) => ({ ...c, number: e.target.value }))} />
      <div style={{ display: 'flex', gap: 6 }}>
        <input style={input} placeholder="MM/YY" inputMode="numeric" maxLength={5} value={card.expiry}
          onChange={(e) => {
            const d = e.target.value.replace(/\D/g, '').slice(0, 4);
            setCard((c) => ({ ...c, expiry: d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d }));
          }} />
        <input style={input} placeholder="CVC" inputMode="numeric" maxLength={4} value={card.code} onChange={(e) => setCard((c) => ({ ...c, code: e.target.value }))} />
      </div>
      <div style={{ fontSize: 11, color: t.muted }}>Card details are tokenized in your browser and sent directly to Authorize.net — they never touch our servers.</div>
    </div>}

    {subQuotes.map(({ interval: iv, quote: sq }) => {
      const per = iv === 30 ? '/ month' : `/ ${iv} days`;
      if (!sq) {
        return <div key={iv} style={{ ...panel, marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: t.accent, marginBottom: 6 }}>Recurring — every {iv} days</div>
          <div style={{ fontSize: 12, color: t.muted }}>These subscription items renew every {iv} days — the recurring total will be confirmed at payment.</div>
        </div>;
      }
      return <div key={iv} style={{ ...panel, marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: t.accent, marginBottom: 6 }}>Recurring totals{subQuotes.length > 1 ? ` — every ${iv} days` : ''}</div>
        <div style={{ fontSize: 13, color: t.muted, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Subtotal</span><span>{money(sq.subtotal - (sq.discount || 0))} {per}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Shipping{sq.shippingLabel ? ` via ${sq.shippingLabel}` : ''}</span><span>{sq.shippingOk === false ? 'Not available' : (sq.shipping === 0 ? 'Free' : `${money(sq.shipping)} ${per}`)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: t.dark ? '#fff' : t.ink }}><span>Recurring total</span><span>{money(sq.total)} {per}</span></div>
        </div>
        <div style={{ fontSize: 11, color: t.muted, marginTop: 6 }}>Charged automatically every {iv} days for these subscription items — your card is saved securely at Authorize.net. Today you pay the full order total below.</div>
      </div>;
    })}

    {skin.mkt.showEarningBox && quote && (quote.pointsToEarn > 0 || subQuotes.some(({ quote: sq }) => sq && sq.pointsToEarn > 0)) && <div style={{ ...panel, marginBottom: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: t.accent, marginBottom: 6 }}>Earning Points</div>
      <div style={{ fontSize: 13, color: t.muted, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {quote.pointsToEarn > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>You earn today</span><span style={{ fontWeight: 700, color: t.dark ? '#fff' : t.ink }}>{Number(quote.pointsToEarn).toLocaleString()} points</span></div>}
        {subQuotes.filter(({ quote: sq }) => sq && sq.pointsToEarn > 0).map(({ interval: iv, quote: sq }) => (
          <div key={iv} style={{ display: 'flex', justifyContent: 'space-between' }}><span>Recurring</span><span>{Number(sq.pointsToEarn).toLocaleString()} points {iv === 30 ? '/ month' : `/ ${iv} days`}</span></div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: t.muted, marginTop: 6 }}>Points are credited to your Arego Points balance each time an order is successfully paid.</div>
    </div>}

    {/* Same picker as the member checkout path — only shown when the
        matched zone genuinely offers more than one priced option. Was
        missing entirely from guest checkout (shippingMethodId/quote both
        exist here as props, but nothing ever rendered a way to use them). */}
    {quote && quote.shippingOptions && quote.shippingOptions.length > 1 && <div style={{ ...panel, marginBottom: 10, padding: '10px 14px' }}>
      <style>{MYUS_RADIO_CSS}</style>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: t.accent, marginBottom: 8 }}>Shipping Method</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {quote.shippingOptions.map((opt) => {
          const selected = (shippingMethodId || quote.shippingMethodId) === opt.id;
          return <label key={opt.id} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, cursor: 'pointer', color: t.dark ? '#fff' : t.ink, padding: '2px 0' }}>
            <input className="myus-radio-input" type="radio" name="shippingMethod" checked={selected} onChange={() => setShippingMethodId(opt.id)} />
            <span className="myus-radio-dot"></span>
            <span style={{ flex: 1 }}>{opt.title}</span>
            <span style={{ fontWeight: 600 }}>{opt.cost === 0 ? 'Free' : money(opt.cost)}</span>
          </label>;
        })}
      </div>
    </div>}

    </div>

    <div className={R}>
    {quote && <div style={{ fontSize: 13, color: t.muted, display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Subtotal</span><span>{money(quote.subtotal)}</span></div>
      {quote.discount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Coupon</span><span>−{money(quote.discount)}</span></div>}
      {/* shippingOk false = no zone covers the picked country — never render
          the zero as "Free"; the pay button below is blocked for it too. */}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Shipping{quote.shippingLabel ? ` (${quote.shippingLabel})` : ''}</span><span>{quote.shippingOk === false ? 'Not available' : (quote.shipping === 0 ? 'Free' : money(quote.shipping))}</span></div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, color: t.dark ? '#fff' : t.ink }}><span>Total</span><span>{money(quote.total)}</span></div>
    </div>}

    <FreeShipNotice cfg={readFreeShip(skin.mkt)} quote={quote} isHipaa={skin.isHipaa}
      destinationKnown={!!country} marks={PAYMENT_MARKS} />

    {error && <div style={{ background: '#fbe9e9', color: '#b02a2a', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 10 }}>{error}</div>}

    <button onClick={submit} disabled={paying || !quote || noShip} style={{
      ...(ctaOn ? CTA_STACK : { display: 'block' }),
      width: '100%', textAlign: 'center', fontSize: 15, fontWeight: 700,
      background: t.accent, color: '#fff', borderRadius: 99, padding: 13, border: 'none',
      cursor: paying || noShip ? 'default' : 'pointer', opacity: paying || noShip ? 0.55 : 1,
    }}>
      <span>{paying ? 'Processing…' : noShip ? 'Shipping unavailable for your country' : `Create account & pay ${quote ? money(quote.total) : ''}`}</span>
      {/* Same suppression rule as the member checkout: the button's own blocked
          states, plus no-quote-yet (the label is a bare "Create account & pay")
          and a zero total. */}
      {ctaOn && <CtaSubline cfg={cta} pct={ctaPct} show={!paying && !noShip && !!quote && quote.total > 0} />}
    </button>
    </div>
  </div>;

  return ckNatural ? <div className="ck-natural-bg">{ckColumns(body)}</div> : body;
};
